import React, { useEffect, useRef, useState } from "react";
import {
  registerPublicCustomer, registrationError, requestRegistrationOtp, verifyRegistrationOtp, fetchRegistrationForm,
} from "../api/customerRegistrationApi";
import { SignaturePad } from "./PublicFormControls";
import RegistrationQuestions from "./RegistrationQuestions";

const EMPTY = { name: "", gender: "", email: "", date_of_birth: "", notes: "", dnd: false };
const inputClass = "block min-w-0 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[13px] leading-[18px] focus:border-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-100 disabled:bg-slate-100";
const buttonClass = "rounded-lg bg-[#096dea] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50";
const RELATIONS = [
  ["father", "Father"], ["mother", "Mother"], ["parent", "Parent"], ["spouse", "Spouse"],
  ["sibling", "Sibling"], ["son", "Son"], ["daughter", "Daughter"], ["guardian", "Guardian"], ["other", "Other"],
];

export default function PublicCustomerRegistration({ botId }) {
  const [customer, setCustomer] = useState(EMPTY);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [proof, setProof] = useState(null);
  const [verified, setVerified] = useState(null);
  const [contactId, setContactId] = useState("");
  const [relationship, setRelationship] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [resendAt, setResendAt] = useState(0);
  const [clock, setClock] = useState(Date.now());
  const [registrationForm, setRegistrationForm] = useState(null);
  const [formLoading, setFormLoading] = useState(true);
  const [formLoadError, setFormLoadError] = useState("");
  const [answers, setAnswers] = useState({});
  const [signature, setSignature] = useState("");
  const [accepted, setAccepted] = useState(false);
  const generation = useRef(0);
  const matches = verified?.customers || [];
  const remaining = Math.max(0, Math.ceil((resendAt - clock) / 1000));
  const expired = verified && Date.parse(verified.expires_at) <= clock;

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => {
      clearInterval(timer);
      generation.current += 1;
    };
  }, []);

  useEffect(() => {
    let active = true;
    setFormLoading(true);
    fetchRegistrationForm(botId).then((response) => {
      if (active) { setRegistrationForm(response.data); setFormLoadError(""); }
    }).catch((err) => {
      if (active) setFormLoadError(registrationError(err));
    }).finally(() => { if (active) setFormLoading(false); });
    return () => { active = false; };
  }, [botId]);

  const update = (key, value) => setCustomer((current) => ({ ...current, [key]: value }));
  const changePhone = (value) => {
    generation.current += 1;
    setPhone(value);
    setCode("");
    setProof(null);
    setVerified(null);
    setContactId("");
    setRelationship("");
    setError("");
  };
  const sendOtp = async () => {
    const request = ++generation.current;
    setBusy("send");
    setError("");
    setProof(null);
    setVerified(null);
    setContactId("");
    setRelationship("");
    setCode("");
    try {
      const response = await (registrationForm
        ? requestRegistrationOtp(botId, phone, registrationForm.template_version_id)
        : requestRegistrationOtp(botId, phone));
      if (generation.current !== request) return;
      const data = response.data;
      setProof({ session_id: data.session_id, session_token: data.session_token });
      setResendAt(Date.now() + data.resend_after * 1000);
      setClock(Date.now());
    } catch (err) {
      if (generation.current === request) setError(registrationError(err));
    } finally {
      if (generation.current === request) setBusy("");
    }
  };
  const verifyOtp = async () => {
    const request = generation.current;
    setBusy("verify");
    setError("");
    try {
      const response = await verifyRegistrationOtp(botId, proof, code);
      if (generation.current !== request) return;
      setVerified(response.data);
      if (response.data.registration_form) setRegistrationForm(response.data.registration_form);
      setContactId(response.data.customers.length === 1 ? response.data.customers[0].id : "");
    } catch (err) {
      if (generation.current === request) setError(registrationError(err));
    } finally {
      if (generation.current === request) setBusy("");
    }
  };
  const submit = async (event) => {
    event.preventDefault();
    if (!verified || expired) {
      setError("Verify the contact number before registering.");
      return;
    }
    if (matches.length && (!contactId || !relationship)) {
      setError("Select the existing contact and their relationship to the new customer.");
      return;
    }
    if (registrationForm && (!signature || !accepted)) {
      setError("Accept the declaration and draw your signature before submitting.");
      return;
    }
    setBusy("submit");
    setError("");
    try {
      const details = {
        ...customer, name: customer.name.trim(), gender: customer.gender || null,
        email: customer.email.trim() || null, date_of_birth: customer.date_of_birth || null,
        notes: customer.notes.trim() || null,
      };
      const response = await (registrationForm
        ? registerPublicCustomer(botId, proof, details, contactId, relationship, {
          form_data: answers, signature_png: signature, accepted,
        })
        : registerPublicCustomer(botId, proof, details, contactId, relationship));
      setResult(response.data);
      setProof(null);
      setVerified(null);
      setCode("");
    } catch (err) {
      setError(registrationError(err));
    } finally {
      setBusy("");
    }
  };

  if (result) return <section id="registration" aria-label="Customer registration" className="border-t border-slate-200 p-8">
    <h2 className="text-xl font-bold text-slate-900">{result.already_registered ? "Already registered" : "Registration successful"}</h2>
    <p role="status" className="mt-2 text-sm text-slate-700">{result.already_registered
      ? result.form_request_id
        ? "Your signed registration form was saved to your existing customer record. Your customer details were not changed."
        : "Your existing customer record was found. No duplicate was created and your details were not changed."
      : "The customer has been registered. Thank you."}</p>
    {result.form_request_id && <p className="mt-2 text-sm text-slate-700">Signed registration form saved. {result.email_queued
      ? "A PDF email has been queued for the clinic."
      : "Automatic email is not enabled for this clinic. The clinic can view and download your form."}</p>}
    <button type="button" className={`${buttonClass} mt-4`} onClick={() => {
      setResult(null); setCustomer(EMPTY); changePhone(""); setResendAt(0);
      setAnswers({}); setSignature(""); setAccepted(false);
    }}>Register another customer</button>
  </section>;

  return <section id="registration" aria-label="Customer registration" className="px-4 py-4 sm:px-6">
    <h2 className="text-center text-lg font-bold text-slate-900">PATIENT REGISTRATION FORM</h2>
    <p className="mt-1 text-xs text-slate-600">Complete the patient details, medical history and signature. Verify your contact number via WhatsApp. * Required fields.</p>
    {formLoading && <p role="status">Loading registration form...</p>}
    {formLoadError && <p role="alert" className="mt-4 text-sm text-rose-700">{formLoadError} Reload the page to retry.</p>}
    <form onSubmit={submit} className="mt-3 space-y-2">
      <fieldset disabled={Boolean(busy)} className="min-w-0 space-y-2">
        <div>
        {registrationForm && <h3 className="registration-section-heading">{registrationForm.definition.blocks?.[0]?.type === "heading"
          ? registrationForm.definition.blocks[0].text : "Patient details"}</h3>}
        <label className="registration-row text-sm font-medium text-slate-700"><span>Customer name *</span>
          <input required maxLength={200} autoComplete="name" className={inputClass} value={customer.name} onChange={(e) => update("name", e.target.value)} />
        </label>
          <label className="registration-row text-sm font-medium text-slate-700"><span>Gender</span>
            <select className={inputClass} value={customer.gender} onChange={(e) => update("gender", e.target.value)}>
              <option value="">Not specified</option>
              <option value="male">Male</option><option value="female">Female</option>
              <option value="nonbinary">Non-binary</option><option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
          </label>
          <label className="registration-row text-sm font-medium text-slate-700"><span>Date of birth</span>
            <input type="date" max={new Date(clock).toISOString().slice(0, 10)} className={inputClass}
              value={customer.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} />
          </label>
        <label className="registration-row text-sm font-medium text-slate-700"><span>Email</span>
          <input type="email" maxLength={255} className={inputClass} value={customer.email} onChange={(e) => update("email", e.target.value)} />
        </label>
        {!registrationForm && <label className="registration-row text-sm font-medium text-slate-700"><span>Notes (optional)</span>
          <textarea maxLength={2000} rows={2} className={inputClass} value={customer.notes} onChange={(e) => update("notes", e.target.value)} />
        </label>}
        </div>
        {registrationForm && <RegistrationQuestions definition={registrationForm.definition} values={answers} omitInitialHeading
          onChange={(key, value) => setAnswers((previous) => ({ ...previous, [key]: value }))} />}
        {registrationForm && <section className="space-y-2">
          <h3 className="registration-section-heading">Declaration and signature</h3>
          <p className="whitespace-pre-wrap text-xs leading-5 text-slate-700">{registrationForm.ui_config.consent_acknowledgement}</p>
          <label className="flex items-start gap-2 text-xs"><input type="checkbox" required checked={accepted}
            onChange={(event) => setAccepted(event.target.checked)} />I have read and accept the declaration.</label>
          <div className="registration-row">
            <span className="text-sm font-medium text-slate-700">Patient / guardian signature *</span>
            <SignaturePad onChange={setSignature} compact />
          </div>
        </section>}
        <div className="border-t border-slate-200 pt-2 space-y-2">
          <div className="registration-row">
          <label htmlFor="registration-contact-phone" className="text-sm font-medium text-slate-700">Verify contact number *</label>
          <div className="flex min-w-0 flex-wrap gap-2">
            <input required type="tel" maxLength={30} autoComplete="tel" placeholder="+91 9876543210"
              id="registration-contact-phone" className={`${inputClass} flex-1 basis-36`} value={phone} onChange={(e) => changePhone(e.target.value)} />
          <button type="button" className={buttonClass} disabled={!phone.trim() || remaining > 0 || formLoading || Boolean(formLoadError)}
            onClick={sendOtp}>{busy === "send" ? "Sending..." : proof || verified ? "Resend OTP" : "Send OTP"}</button>
          </div>
          </div>
          <p className="text-xs text-slate-600">For a child, use a parent's number. Include the country code outside India.</p>
          {remaining > 0 && <p className="text-xs text-slate-600">You can request another code in {remaining} seconds.</p>}
          {proof && !verified && <>
            <p role="status" className="text-sm text-slate-700">OTP sent via WhatsApp. The code expires in 5 minutes.</p>
            <div className="registration-row">
            <label htmlFor="registration-otp" className="text-sm font-medium text-slate-700">OTP</label>
            <div className="flex min-w-0 flex-wrap gap-2">
              <input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6}
                id="registration-otp" className={`${inputClass} flex-1 basis-28`} value={code} onChange={(e) => setCode(e.target.value)} />
            <button type="button" className={buttonClass} disabled={!/^[0-9]{6}$/.test(code)} onClick={verifyOtp}>
              {busy === "verify" ? "Verifying..." : "Verify OTP"}
            </button>
            </div>
            </div>
          </>}
          {verified && !expired && <p role="status" className="text-sm font-semibold text-emerald-700">Contact number verified.</p>}
          {expired && <p role="alert" className="text-sm text-rose-700">Verification expired. Request and verify a new code.</p>}
        </div>
        {verified && matches.length > 0 && <div className="space-y-2 rounded border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-slate-700">This verified number belongs to an existing customer. Choose who you are and your relationship to the person being registered.</p>
          <label className="registration-row text-sm font-medium text-slate-700"><span>Existing customer (contact person)</span>
            <select required className={inputClass} value={contactId} onChange={(e) => setContactId(e.target.value)}>
              <option value="">Choose an existing customer</option>
              {matches.map((person) => <option key={person.id} value={person.id}>
                {person.name || "Unnamed customer"}{person.registration_number ? ` (${person.registration_number})` : ""}
              </option>)}
            </select>
          </label>
          <label className="registration-row text-sm font-medium text-slate-700"><span>I am the following relation to the new customer</span>
            <select required className={inputClass} value={relationship} onChange={(e) => setRelationship(e.target.value)}>
              <option value="">Choose a relationship</option>
              {RELATIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              <option value="self">This is me (already registered)</option>
            </select>
          </label>
          <p className="text-xs text-slate-600">For example, select Father if you are registering your son. Your existing details will not replace the new customer's details. A guardian designation does not grant consent-signing authority.</p>
        </div>}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3">
        <label className="flex flex-1 gap-2 text-xs text-slate-700">
          <input type="checkbox" checked={customer.dnd} onChange={(e) => update("dnd", e.target.checked)} />
          Do not send optional greetings or promotions to this customer.
        </label>
        <button type="submit" className={buttonClass} disabled={formLoading || Boolean(formLoadError) || !verified || expired || (matches.length > 0 && (!contactId || !relationship))}>
          {busy === "submit" ? "Registering..." : "Submit registration"}
        </button>
        </div>
      </fieldset>
      {error && <p role="alert" className="text-sm text-rose-700">{error}</p>}
    </form>
  </section>;
}
